import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { Badge, Button, Checkbox, Chip, StatePanel } from "../../components/ui";
import {
  boxOptions,
  sellerTypeOptions,
  sourceOptions,
} from "../../product/constants";
import "./marketplace-watch.scss";

/**
 * Marketplace watching for ONE Core List reference (Alex, 2026-10-08).
 *
 * "I want all existing marketplaces of Buy Alerts covered in Core List."
 *
 * The buy target is already set above this: per-year ceilings, in the
 * merchant's own currency. This section says WHERE ELSE to look for them. The
 * same filter set Buy Alerts always offered — sources, condition, box/papers,
 * seller type, deals-only — sits on the reference it describes, so a watch is
 * stated once instead of twice.
 *
 * "Trade" is deliberately NOT in this list. Trade-group quotes are matched
 * continuously by the backend against the same targets, and their results are
 * the rows above. Offering it here would imply you could switch off the thing
 * the page is built on.
 *
 * OFF BY DEFAULT, per reference. A store with 116 references must not start
 * hitting a rate-limited scraper 116 times because a release landed.
 */

// Trade sits WITH the marketplaces, not above them (Alex, 2026-10-09:
// "unify both trade and market selection"). That it is matched by our own
// backend rather than by the external scraper is a difference in plumbing,
// not in what the merchant is choosing, which is simply where to look.
const SOURCES = sourceOptions;

/** The one source matched by our own backend rather than the scraper. */
const TRADE = "trade";

/**
 * CONDITION IS GONE (Alex, 2026-10-12: "remove Condition as an option").
 *
 * It was the weakest of the three and the most misleading. A dealer's own
 * condition wording is free text that the intake maps optimistically, so
 * filtering a watch on it quietly dropped real quotes that simply had not been
 * classified — on a page whose entire complaint history is "it says zero and I
 * do not believe it".
 *
 * The stored `conditions` array is deliberately NOT cleared here or on the
 * server: it is still sent back unchanged on save, so an existing watch keeps
 * whatever it had and nothing is destroyed by a UI decision. The match view
 * still SHOWS each quote's condition, which is where it was always more
 * useful — reading it beside a price tells you something, filtering on it
 * before you have seen the price does not.
 */
const CHECK_GROUPS = [
  { key: "delivery_sets", label: "Box & papers", options: boxOptions },
  { key: "seller_types", label: "Seller", options: sellerTypeOptions },
];

const toggleIn = (list, id) =>
  list.includes(id) ? list.filter((v) => v !== id) : [...list, id];

const MarketplaceWatch = ({ itemId, marketplace, hasTargets, registerSave }) => {
  const initial = useMemo(
    () => ({
      // A reference that has never been through this chooser watches the
      // trade groups and nothing else — which is what it has always done, and
      // what the backend assumes when `sources` is absent. Seeding it here
      // means the chips show the truth rather than an empty row implying
      // nothing is being watched.
      sources: marketplace?.sources?.length
        ? marketplace.sources
        : [TRADE],
      conditions: marketplace?.conditions || [],
      delivery_sets: marketplace?.delivery_sets || [],
      seller_types: marketplace?.seller_types || [],
      underpriced: !!marketplace?.underpriced,
    }),
    [marketplace]
  );

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Re-seed when the server's view changes, but never over something the user
  // is part-way through editing — the same rule the target editor follows.
  useEffect(() => {
    if (saving) return;
    setForm(initial);
  }, [initial, saving]);

  const patch = (next) => {
    setSaved(false);
    setForm((current) => ({ ...current, ...next }));
  };

  const matches = marketplace?.matches || [];

  // One line instead of two rows of chips. Names the group only when it is
  // actually narrowed — "Any condition · Any box · Any seller" is three times
  // the words for no information.
  const filterSummary = useMemo(() => {
    const label = (key, options) =>
      form[key]
        .map((id) => options.find((o) => o.id === id)?.name || id)
        .join(", ");

    const parts = CHECK_GROUPS.map((g) => label(g.key, g.options)).filter(Boolean);

    if (form.underpriced) parts.push("deals only");

    return parts.length ? parts.join(" · ") : "Anything";
  }, [form]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`/api/core-list/${itemId}/sources`, {
        // DERIVED, not a separate switch. Once Trade is one chip among the
        // rest, a toggle saying "also watch marketplaces" is a second way to
        // express the same thing and the two can disagree: chips showing
        // Chrono24 selected while the toggle quietly says off. Picking any
        // marketplace IS switching it on.
        marketplace_enabled: form.sources.some((s) => s !== TRADE),
        sources: form.sources,
        conditions: form.conditions,
        delivery_sets: form.delivery_sets,
        seller_types: form.seller_types,
        underpriced: form.underpriced,
      });
      setSaved(true);
    } catch (e) {
      setError(
        e?.response?.data?.message || "Couldn't save where to look."
      );
      // Rethrown so the one Save button reports a single outcome: the targets
      // saving while the sources quietly did not is the worst of both.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  // Hand the save up, so the panel's single Save targets button persists the
  // price and where to look together.
  useEffect(() => {
    if (registerSave) registerSave(save);
  });

  useEffect(() => {
    if (!saved) return undefined;
    const timer = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [saved]);

  return (
    <div className="mp-watch">
      <div className="mp-watch__copy">
        <h4 className="mp-watch__title">Where to look</h4>
        <span className="mp-watch__hint">
          {hasTargets
            ? "Every source is searched at the ceilings you set — one search per price band. Trade groups are matched continuously; marketplaces a few times a day."
            : "Set a buy target first: there is nothing to search under until there is a ceiling."}
        </span>
      </div>

      {hasTargets && (
        <>
          <div className="mp-watch__group">
            <span className="mp-watch__group-label">Sources</span>
            <div className="mp-watch__chips">
              {SOURCES.map((source) => {
                const on = form.sources.includes(source.id);
                return (
                  <Chip
                    key={source.id}
                    selected={on}
                    onClick={() => patch({ sources: toggleIn(form.sources, source.id) })}
                  >
                    {source.name}
                  </Chip>
                );
              })}
            </div>
          </div>

          {/* OPEN BY DEFAULT (Alex, 2026-10-12: "the entire filters and
              sources selection has to be moved up above the SAVE TARGET
              buttons and shown open by default to choose").

              Still a <details>, so it can be collapsed once set, and still
              summarised when shut — but it no longer starts shut. Hiding the
              filters behind a disclosure made them easy to miss at the moment
              they are decided, which is while the target is being set. Losing
              Condition took a row out, so the open state costs less than it
              did when this was three groups. */}
          <details className="mp-watch__filters" open>
            <summary>
              <span className="mp-watch__group-label">Filters</span>
              <span className="mp-watch__summary">{filterSummary}</span>
            </summary>

            {CHECK_GROUPS.map((group) => (
              <div className="mp-watch__group" key={group.key}>
                <span className="mp-watch__group-label">{group.label}</span>
                <div className="mp-watch__chips">
                  {group.options.map((option) => {
                    const on = form[group.key].includes(option.id);
                    return (
                      <Chip
                        key={option.id}
                        selected={on}
                        onClick={() =>
                          patch({ [group.key]: toggleIn(form[group.key], option.id) })
                        }
                      >
                        {option.name}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            ))}

            <Checkbox
              className="mp-watch__deals"
              checked={form.underpriced}
              onChange={(next) => patch({ underpriced: next })}
              label="Only tell me about under-priced listings"
            />
          </details>

          {/* No Save button of its own: "Save targets" above persists the
              price and where to look in one click (Alex, 2026-10-09). Two
              buttons left no way to tell which had taken. */}
          {form.sources.length === 0 && (
            <span className="mp-watch__note">
              Pick at least one source, or this reference is watched nowhere.
            </span>
          )}
        </>
      )}

      {error && (
        <p className="mp-watch__error" role="alert">
          {error}
        </p>
      )}

      {/* Found listings. Prices are shown exactly as the marketplace stated
          them: alert_listings.price is a string and its currency is free text,
          so converting would produce a confident figure that is sometimes
          wrong. The link is the authority. */}
      {matches.length > 0 && (
        <div className="mp-watch__matches">
          <span className="mp-watch__group-label">
            Found on marketplaces
            <Badge tone="success" className="mp-watch__count">
              {matches.length}
            </Badge>
          </span>
          <ul className="mp-watch__list">
            {matches.map((m) => (
              <li className="mp-watch__match" key={m.listing_id}>
                <a href={m.link} target="_blank" rel="noopener noreferrer">
                  <span className="mp-watch__match-title">{m.title}</span>
                </a>
                <span className="mp-watch__match-meta">
                  {m.source}
                  {m.country ? ` · ${m.country}` : ""}
                  {m.seen ? ` · ${m.seen}` : ""}
                </span>
                <span className="mp-watch__match-price">
                  {m.currency} {m.price}
                  {m.underpriced && (
                    <Badge tone="success" className="mp-watch__deal">
                      Deal
                    </Badge>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasTargets && marketplace?.enabled && matches.length === 0 && (
        <StatePanel
          state="empty"
          compact
          title="Nothing found on marketplaces yet"
          description="Searches run a few times a day — Chrono24 is rate-limited, so results arrive on that cadence rather than instantly."
        />
      )}
    </div>
  );
};

export default MarketplaceWatch;
